import type { ClassificationResult } from '@classification-testing/domain';
import { TimestampDisplay } from '@core/components';
import { getLanguageDisplayInfo, getLanguageName } from '@core/utils';
import { Text, Badge, BadgeSize, Icon, Warning, IconSize } from '@soteria/design-system-ve';

import * as styles from './ClassificationResultItemV2.css';

/** Get display name for a content type */
export function getContentTypeDisplayName(contentType: string): string {
  const typeMap: Record<string, string> = {
    text: 'Text',
    image: 'Image',
    audio: 'Audio',
    mixed: 'Mixed',
    username: 'Username',
  };

  return typeMap[contentType] || 'Content';
}

/** Check if policies exist for the result */
export function hasPolicyData(result: ClassificationResult): boolean {
  return (result.policies?.length ?? 0) > 0 || (result.nonTriggeredPolicies?.length ?? 0) > 0;
}

/**
 * Header metadata component — renders Topic, Language, Content Type
 * as label + badge items with pipe separators for ContentCard.Header
 */
export function ClassificationHeaderMeta({ result }: { result: ClassificationResult }) {
  const languageInfo = getLanguageDisplayInfo(result.languageDetected, result.languageClassified);
  const showLanguage = result.languageDetected || result.languageClassified;

  return (
    <div className={styles.headerMetaRow}>
      {result.topicName && (
        <>
          <div className={styles.headerMetaItem}>
            <Text className={styles.metadataLabel}>Topic:</Text>
            <Badge size={BadgeSize.Small} text={result.topicName} />
          </div>
          <Text aria-hidden="true" as="span" className={styles.headerMetaSeparator}>
            |
          </Text>
        </>
      )}

      {showLanguage && (
        <>
          <div className={styles.headerMetaItem}>
            <Text className={styles.metadataLabel}>Language:</Text>
            <Badge size={BadgeSize.Small} text={getLanguageName(result.languageClassified || result.languageDetected)} />
            {languageInfo.showTranslationIndicator && <Icon icon={Warning} iconSize={IconSize.XSmall} title={languageInfo.tooltipText} />}
          </div>
          <Text aria-hidden="true" as="span" className={styles.headerMetaSeparator}>
            |
          </Text>
        </>
      )}

      <div className={styles.headerMetaItem}>
        <Text className={styles.metadataLabel}>Content Type:</Text>
        <Badge size={BadgeSize.Small} text={getContentTypeDisplayName(result.contentType).toUpperCase()} />
      </div>

      {result.timestamp && (
        <>
          <Text aria-hidden="true" as="span" className={styles.headerMetaSeparator}>
            |
          </Text>
          <div className={styles.headerMetaItem}>
            <TimestampDisplay timestamp={result.timestamp} />
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Triggered policies section content — renders policy badges
 */
export function PolicyBadgesSection({ result }: { result: ClassificationResult }) {
  const hasTriggeredPolicies = (result.policies?.length ?? 0) > 0;

  return (
    <div className={styles.sectionBadges}>
      {!hasTriggeredPolicies ? (
        <Badge size={BadgeSize.Small} text="None" />
      ) : (
        result.policies?.map(policy => <Badge key={policy.id} size={BadgeSize.Small} text={policy.name} title={policy.description} />)
      )}
    </div>
  );
}

/**
 * Label badges section content — renders classification label badges
 */
export function LabelBadgesSection({ result }: { result: ClassificationResult }) {
  if (!result.labels || result.labels.length === 0) {
    return null;
  }

  return (
    <div className={styles.sectionBadges}>
      {result.labels.map(label => (
        <Badge
          key={label.label_id || label.label_name}
          size={BadgeSize.Small}
          text={`${(label.label_name || '').toLowerCase()} ${label.severity || ''}`}
          title={`${label.label_name || ''} (Severity: ${label.severity || ''}${label.confidence ? `, Confidence: ${(label.confidence * 100).toFixed(4)}%` : ''})`}
        />
      ))}
    </div>
  );
}
